import { useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from './AuthProvider'

function AppLoadingScreen() {
  return (
    <main className="loading-page" aria-live="polite">
      <div className="loading-brand">
        <strong>Swell AI</strong>
        <span>Checking secure access…</span>
      </div>
    </main>
  )
}

function AccessLoadingError() {
  const { error, refreshIdentity, signOut } = useAuth()
  const [retrying, setRetrying] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const retry = async () => {
    setRetrying(true)
    try {
      await refreshIdentity()
    } finally {
      setRetrying(false)
    }
  }

  const leave = async () => {
    setSigningOut(true)
    try {
      await signOut()
    } finally {
      setSigningOut(false)
    }
  }

  return (
    <main className="support-auth-page">
      <section className="support-auth-form" aria-labelledby="access-loading-title">
        <div className="support-auth-card">
          <p className="eyebrow">Secure access</p>
          <h2 id="access-loading-title">We couldn’t load your access</h2>
          <p className="muted">
            You are signed in, but Swell could not load your account access. Your password does not need to be changed.
          </p>
          {error ? <div className="inline-alert danger">{error}</div> : null}
          <div className="support-auth-actions">
            <button className="button primary" type="button" disabled={retrying || signingOut} onClick={() => { void retry() }}>
              {retrying ? 'Trying again…' : 'Try again'}
            </button>
            <button className="button secondary" type="button" disabled={retrying || signingOut} onClick={() => { void leave() }}>
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      </section>
    </main>
  )
}

export function AuthenticatedRoute() {
  const { session, profile, loading, error } = useAuth()
  const location = useLocation()

  if (loading) return <AppLoadingScreen />
  if (!session) {
    const returnTo = `${location.pathname}${location.search}`
    return <Navigate to={`/auth/login?returnTo=${encodeURIComponent(returnTo)}`} replace />
  }

  if (error || !profile) return <AccessLoadingError />

  if (!profile.initialPasswordSetAt) {
    const returnTo = `${location.pathname}${location.search}`
    return <Navigate to={`/auth/update-password?firstAccess=1&returnTo=${encodeURIComponent(returnTo)}`} replace />
  }

  return <Outlet />
}
